import React, { useEffect, useState } from "react";
import axios from "axios";
import { API_ENDPOINTS } from "../config/api";
import {
  FaPhoneAlt,
  FaEnvelope,
  FaMapMarkerAlt,
  FaFacebook,
  FaInstagram,
  FaYoutube,
  FaPlus,
  FaTrash,
  FaSave,
  FaCheckCircle,
  FaInfoCircle,
  FaGlobe,
  FaRegMap
} from "react-icons/fa";
import "../styles/ManageContact.css";

const ManageContact = () => {
  const API_URL = API_ENDPOINTS.CONTACT;

  /* ================= STATE ================= */
  const [header, setHeader] = useState({
    title: "",
    subtitle: "",
  });

  const [phones, setPhones] = useState([]);
  const [emails, setEmails] = useState([]);
  const [address, setAddress] = useState("");
  const [mapLink, setMapLink] = useState("");

  const [socials, setSocials] = useState({
    facebook: "",
    instagram: "",
    youtube: "",
  });

  const [loading, setLoading] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Redesign Tab state
  const [activeTab, setActiveTab] = useState("general"); // "general" | "map" | "socials" | "header"

  /* ================= FETCH DATA ================= */
  useEffect(() => {
    fetchContact();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchContact = async () => {
    try {
      setLoading(true);
      const res = await axios.get(API_URL);
      const data = res.data;

      if (data) {
        setHeader(data.header || { title: "", subtitle: "" });
        setPhones(data.phones || []);
        setEmails(data.emails || []);
        setAddress(data.address || "");
        setMapLink(data.mapLink || "");
        setSocials(
          data.socials || {
            facebook: "",
            instagram: "",
            youtube: "",
          }
        );
      }
    } catch (error) {
      console.log("FETCH ERROR:", error.message);
    } finally {
      setLoading(false);
    }
  };

  /* ================= HELPERS ================= */
  const updatePhone = (i, value) => {
    const updated = [...phones];
    updated[i] = value;
    setPhones(updated);
  };

  const addPhone = () => {
    setPhones([...phones, ""]);
  };

  const deletePhone = (i) => {
    setPhones(phones.filter((_, index) => index !== i));
  };

  const updateEmail = (i, value) => {
    const updated = [...emails];
    updated[i] = value;
    setEmails(updated);
  };

  const addEmail = () => {
    setEmails([...emails, ""]);
  };

  const deleteEmail = (i) => {
    setEmails(emails.filter((_, index) => index !== i));
  };

  /* ================= SAVE ================= */
  const handleSave = async () => {
    try {
      setLoading(true);
      setSaveSuccess(false);

      await axios.post(API_URL, {
        header,
        phones,
        emails,
        address,
        mapLink,
        socials,
      });

      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
      alert("🎉 Contact Page Updated Successfully!");
      fetchContact();
    } catch (error) {
      console.log("SAVE ERROR:", error.message);
      alert("❌ Failed To Save Contact Details");
    } finally {
      setLoading(false);
    }
  };

  /* ================= RENDER LOADING ================= */
  if (loading && phones.length === 0) {
    return (
      <div className="contact-loading">
        <div className="spinner"></div>
        <h2>Loading Contact Registry...</h2>
        <p>Connecting to backend API</p>
      </div>
    );
  }

  // Check if mapLink is embeddable or extract src
  const getMapEmbedUrl = (url) => {
    if (!url) return "";
    if (url.includes("<iframe")) {
      const match = url.match(/src="([^"]+)"/);
      return match ? match[1] : "";
    }
    return url;
  };

  const embedMapUrl = getMapEmbedUrl(mapLink);

  return (
    <div className="manage-contact-container">
      {/* BANNER SECTION */}
      <div className="contact-banner">
        <div className="banner-content">
          <h1>📞 Manage Contact Page</h1>
          <p>Update phone numbers, email addresses, geographic location coordinates, map embed codes, and social channels.</p>
        </div>
        <div className="banner-stats">
          <div className="stat-pill">
            <FaPhoneAlt className="icon" />
            <span><strong>{phones.length}</strong> Phones</span>
          </div>
          <div className="stat-pill">
            <FaEnvelope className="icon" />
            <span><strong>{emails.length}</strong> Emails</span>
          </div>
        </div>
      </div>

      {/* DASHBOARD CONTROLS & TAB SWITCHER */}
      <div className="dashboard-controls">
        <div className="tabs-wrapper">
          <button 
            className={`tab-btn ${activeTab === "general" ? "active" : ""}`}
            onClick={() => setActiveTab("general")}
          >
            <FaPhoneAlt /> Communication Details
          </button>
          <button 
            className={`tab-btn ${activeTab === "map" ? "active" : ""}`}
            onClick={() => setActiveTab("map")}
          >
            <FaMapMarkerAlt /> Temple Location & Map
          </button>
          <button 
            className={`tab-btn ${activeTab === "socials" ? "active" : ""}`}
            onClick={() => setActiveTab("socials")}
          >
            <FaGlobe /> Social Channels
          </button>
          <button 
            className={`tab-btn ${activeTab === "header" ? "active" : ""}`}
            onClick={() => setActiveTab("header")}
          >
            <FaInfoCircle /> Page Header
          </button>
        </div>

        <div className="quick-action-buttons">
          {activeTab === "general" && (
            <>
              <button className="action-btn add-btn-primary" onClick={addPhone}>
                <FaPlus /> Add Phone
              </button>
              <button className="action-btn add-btn-primary" onClick={addEmail}>
                <FaPlus /> Add Email
              </button>
            </>
          )}
          
          <button 
            className={`action-btn save-btn-primary ${loading ? "saving" : ""} ${saveSuccess ? "saved" : ""}`}
            onClick={handleSave}
            disabled={loading}
          >
            {loading ? (
              <>
                <div className="spinner-small"></div> Saving...
              </>
            ) : saveSuccess ? (
              <>
                <FaCheckCircle /> Saved!
              </>
            ) : (
              <>
                <FaSave /> Save Changes
              </>
            )}
          </button>
        </div>
      </div>

      {/* PANEL CONTAINER */}
      <div className="panel-container">
        
        {/* PANEL: PAGE HEADER */}
        {activeTab === "header" && (
          <div className="panel-card fade-in">
            <div className="panel-card-header">
              <h3><FaInfoCircle className="title-icon" /> Page Title & Header</h3>
              <p>Configure the header texts that appear at the top of the contact web page.</p>
            </div>
            
            <div className="panel-card-body">
              <div className="form-group">
                <label>Header Title</label>
                <input
                  type="text"
                  value={header.title || ""}
                  onChange={(e) => setHeader({ ...header, title: e.target.value })}
                  placeholder="e.g. Contact Us"
                />
              </div>

              <div className="form-group">
                <label>Header Subtitle</label>
                <textarea
                  value={header.subtitle || ""}
                  onChange={(e) => setHeader({ ...header, subtitle: e.target.value })}
                  placeholder="e.g. Get in touch with temple administration or priests for offerings and bookings..."
                  rows={4}
                />
              </div>
            </div>
          </div>
        )}

        {/* PANEL: GENERAL COMMUNICATION */}
        {activeTab === "general" && (
          <div className="general-settings-grid fade-in">
            {/* Phones & Emails column */}
            <div className="communication-list-panel">
              
              {/* Phone Numbers List */}
              <div className="inner-panel-card">
                <div className="panel-card-header-compact">
                  <h4><FaPhoneAlt className="icon" /> Phone Numbers</h4>
                </div>
                
                {phones.length === 0 ? (
                  <div className="empty-inner-state">No phone numbers listed.</div>
                ) : (
                  <div className="inputs-list-tray">
                    {phones.map((phone, i) => (
                      <div className="input-item-row" key={i}>
                        <div className="input-with-icon">
                          <FaPhoneAlt className="input-icon" />
                          <input
                            type="text"
                            value={phone}
                            onChange={(e) => updatePhone(i, e.target.value)}
                            placeholder="e.g. +91 98765 43210"
                          />
                        </div>
                        <button className="trash-delete-btn" onClick={() => deletePhone(i)}>
                          <FaTrash />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Emails List */}
              <div className="inner-panel-card">
                <div className="panel-card-header-compact">
                  <h4><FaEnvelope className="icon" /> Email Addresses</h4>
                </div>
                
                {emails.length === 0 ? (
                  <div className="empty-inner-state">No email addresses listed.</div>
                ) : (
                  <div className="inputs-list-tray">
                    {emails.map((email, i) => (
                      <div className="input-item-row" key={i}>
                        <div className="input-with-icon">
                          <FaEnvelope className="input-icon" />
                          <input
                            type="email"
                            value={email}
                            onChange={(e) => updateEmail(i, e.target.value)}
                            placeholder="e.g. contact@gnanasaibaba.org"
                          />
                        </div>
                        <button className="trash-delete-btn" onClick={() => deleteEmail(i)}>
                          <FaTrash />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Address Column */}
            <div className="address-details-panel">
              <div className="panel-card full-height">
                <div className="panel-card-header">
                  <h3><FaMapMarkerAlt className="title-icon" /> Temple Physical Address</h3>
                  <p>Input the full postal address of the temple for visitors.</p>
                </div>
                
                <div className="panel-card-body">
                  <div className="form-group">
                    <label>Street Address & Landmark</label>
                    <textarea
                      value={address || ""}
                      onChange={(e) => setAddress(e.target.value)}
                      placeholder="e.g. Gnana Sai Baba Mandir, Landmark Road, Chennai, Tamil Nadu - 600001..."
                      rows={8}
                      className="address-textarea"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* PANEL: MAP & LOCATION */}
        {activeTab === "map" && (
          <div className="panel-card fade-in">
            <div className="panel-card-header">
              <h3><FaMapMarkerAlt className="title-icon" /> Google Map Integration</h3>
              <p>Paste the Google Maps share link or the iframe embed code to display the interactive map.</p>
            </div>

            <div className="panel-card-body-layout">
              <div className="left-form-panel">
                <div className="form-group">
                  <label>Google Maps Embed Link / Share Link</label>
                  <textarea
                    value={mapLink || ""}
                    onChange={(e) => setMapLink(e.target.value)}
                    placeholder='e.g. https://www.google.com/maps/embed?pb=... or <iframe src="https://www.google.com/...></iframe>'
                    rows={4}
                  />
                  <small className="help-text">
                    Go to Google Maps → Click Share → Select "Embed a map" → Copy the link and paste it here.
                  </small>
                </div>
              </div>

              <div className="right-preview-panel">
                <label className="preview-label-tag">Map Embed Preview</label>
                {embedMapUrl ? (
                  <div className="map-preview-frame-wrapper">
                    <iframe
                      src={embedMapUrl}
                      title="Google Maps Location Preview"
                      frameBorder="0"
                      allowFullScreen
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                      className="map-iframe-element"
                    ></iframe>
                  </div>
                ) : (
                  <div className="preview-placeholder">
                    <FaRegMap className="icon" />
                    <span>No Valid Map Link Provided</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* PANEL: SOCIAL CHANNELS */}
        {activeTab === "socials" && (
          <div className="panel-card social-channels-card fade-in">
            <div className="panel-card-header">
              <h3><FaGlobe className="title-icon" /> Social Media Link Pages</h3>
              <p>Configure links to the official temple social media channels to allow visitors to follow updates.</p>
            </div>

            <div className="social-channels-grid">
              {/* FACEBOOK CARD */}
              <div className={`social-brand-card facebook ${socials.facebook ? "active" : ""}`}>
                <div className="brand-accent-bar"></div>
                <div className="brand-content-wrapper">
                  <div className="brand-header">
                    <div className="brand-icon-container">
                      <FaFacebook className="brand-icon" />
                    </div>
                    <div className="brand-info">
                      <h4>Facebook</h4>
                      <p>Connect with devotees on our official Page</p>
                    </div>
                    <span className={`brand-status-badge ${socials.facebook ? "configured" : "empty"}`}>
                      {socials.facebook ? "Active" : "Not Configured"}
                    </span>
                  </div>
                  <div className="brand-body">
                    <div className="input-with-action">
                      <div className="premium-input-box">
                        <span className="input-prefix-label">URL</span>
                        <input
                          type="text"
                          placeholder="https://facebook.com/your-temple-page"
                          value={socials.facebook || ""}
                          onChange={(e) => setSocials({ ...socials, facebook: e.target.value })}
                        />
                      </div>
                      {socials.facebook && (
                        <a 
                          href={socials.facebook} 
                          target="_blank" 
                          rel="noopener noreferrer" 
                          className="brand-action-btn fb-test"
                        >
                          Test Link
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* INSTAGRAM CARD */}
              <div className={`social-brand-card instagram ${socials.instagram ? "active" : ""}`}>
                <div className="brand-accent-bar"></div>
                <div className="brand-content-wrapper">
                  <div className="brand-header">
                    <div className="brand-icon-container">
                      <FaInstagram className="brand-icon" />
                    </div>
                    <div className="brand-info">
                      <h4>Instagram</h4>
                      <p>Share daily Darshan photos & divine moments</p>
                    </div>
                    <span className={`brand-status-badge ${socials.instagram ? "configured" : "empty"}`}>
                      {socials.instagram ? "Active" : "Not Configured"}
                    </span>
                  </div>
                  <div className="brand-body">
                    <div className="input-with-action">
                      <div className="premium-input-box">
                        <span className="input-prefix-label">URL</span>
                        <input
                          type="text"
                          placeholder="https://instagram.com/your-temple-handle"
                          value={socials.instagram || ""}
                          onChange={(e) => setSocials({ ...socials, instagram: e.target.value })}
                        />
                      </div>
                      {socials.instagram && (
                        <a 
                          href={socials.instagram} 
                          target="_blank" 
                          rel="noopener noreferrer" 
                          className="brand-action-btn insta-test"
                        >
                          Test Link
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* YOUTUBE CARD */}
              <div className={`social-brand-card youtube ${socials.youtube ? "active" : ""}`}>
                <div className="brand-accent-bar"></div>
                <div className="brand-content-wrapper">
                  <div className="brand-header">
                    <div className="brand-icon-container">
                      <FaYoutube className="brand-icon" />
                    </div>
                    <div className="brand-info">
                      <h4>YouTube</h4>
                      <p>Broadcast live events, Bhajans & Aarti streams</p>
                    </div>
                    <span className={`brand-status-badge ${socials.youtube ? "configured" : "empty"}`}>
                      {socials.youtube ? "Active" : "Not Configured"}
                    </span>
                  </div>
                  <div className="brand-body">
                    <div className="input-with-action">
                      <div className="premium-input-box">
                        <span className="input-prefix-label">URL</span>
                        <input
                          type="text"
                          placeholder="https://youtube.com/c/your-temple-channel"
                          value={socials.youtube || ""}
                          onChange={(e) => setSocials({ ...socials, youtube: e.target.value })}
                        />
                      </div>
                      {socials.youtube && (
                        <a 
                          href={socials.youtube} 
                          target="_blank" 
                          rel="noopener noreferrer" 
                          className="brand-action-btn yt-test"
                        >
                          Test Link
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* FLOATING SAVE BAR */}
      <div className="floating-save-bar">
        <div className="save-bar-info">
          <span>Unsaved changes to contact details will be discarded.</span>
        </div>
        <button 
          className={`action-btn save-btn-primary ${loading ? "saving" : ""} ${saveSuccess ? "saved" : ""}`}
          onClick={handleSave}
          disabled={loading}
        >
          {loading ? (
            <>
              <div className="spinner-small"></div> Saving...
            </>
          ) : saveSuccess ? (
            <>
              <FaCheckCircle /> Saved Successfully!
            </>
          ) : (
            <>
              <FaSave /> Save Changes
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default ManageContact;
